// src/components/admin/EventsManagement.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useData } from "../../providers/dataProvider";
import { pageMeta } from "../../config/helmetConfig";
import { updateMetaTags } from "../../utils/metaManager";

function ModalContent({
  eventToDelete,
  setShowDeleteModal,
  handleDeleteEvent,
  eventsLoading,
}) {
  if (eventsLoading) {
    return (
      <Modal.Body className="bg-dark text-white">
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Modal.Body>
    );
  }
  return (
    <>
      <Modal.Header className="bg-dark text-white border-secondary">
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-white">
        Are you sure you want to delete the event:
        <strong> {eventToDelete?.title}</strong>?
        <br />
        This action cannot be undone.
      </Modal.Body>
      <Modal.Footer className="bg-dark text-white border-secondary">
        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDeleteEvent}>
          Delete Event
        </Button>
      </Modal.Footer>
    </>
  );
}

export default function AdminEvents() {
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const { events, removeEvent, eventsLoading, images } = useData(); // Added images
  const navigate = useNavigate();

  const handleEditEvent = (id) => {
    navigate(`/admin/events/edit/${id}`);
  };
  useEffect(() => {
    updateMetaTags(pageMeta.admin);
  }, []);
  const confirmDelete = (event) => {
    setEventToDelete(event);
    setShowDeleteModal(true);
  };

  const handleDeleteEvent = async () => {
    try {
      await removeEvent(eventToDelete.id);
    } catch (error) {
      setError(error.message);
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Function to get image information
  const getImageInfo = (imageId) => {
    if (!imageId || !images || images.length === 0) return "None";
    const image = images.find((img) => img.id === imageId);
    return image ? image.fileName || "Unnamed Image" : "Image not found";
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <h1 className="text-light">Manage Events</h1>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate("/admin/dashboard")}
          >
            Back to Dashboard
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card
            className="bg-dark text-white border-secondary"
            style={{ width: "100%" }}
          >
            <Card.Body>
              <div className="table-responsive">
                <Table variant="dark" bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Event Name</th>
                      <th className="d-none d-md-table-cell">Date & Time</th>
                      <th className="d-none d-md-table-cell">Location</th>
                      <th className="d-none d-lg-table-cell">Image</th>
                      <th style={{ width: "140px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event, index) => (
                      <tr key={event.id}>
                        <td>{event.id}</td>
                        <td>{event.title}</td>
                        <td className="d-none d-md-table-cell">
                          {`${event.month} ${event.day}, ${event.year} • ${event.time}`}
                        </td>
                        <td className="d-none d-md-table-cell">
                          {event.location}
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {getImageInfo(event.image_id)}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEditEvent(event.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => confirmDelete(event)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="bg-dark border-secondary">
              <Button
                variant="success"
                onClick={() => navigate("/admin/events/edit/new")}
              >
                Add New Event
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <ModalContent
          eventToDelete={eventToDelete}
          setShowDeleteModal={setShowDeleteModal}
          handleDeleteEvent={handleDeleteEvent}
          eventsLoading={eventsLoading}
        />
      </Modal>
    </Container>
  );
}
